import { useEffect, useState } from 'react';
import {
  Banner,
  Button,
  ButtonRouterLink,
  Checkbox,
  DiffView,
  Disclosure,
  Markdown,
  Modal,
  MonoLabel,
  NavListItem,
  Skeleton,
  Tag,
  TextAreaField,
  TextField,
  Toast
} from '../components/primitives';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import {
  HARDCODED_DEFAULT_RULE,
  RULE_SOFT_WARN,
  resolveRuleStatus,
  ruleStatusLabel,
  type FunctionRole,
  type RoleRuleVersion
} from '../lib/models/functionRoles';
import {
  SOFT_CAP_THRESHOLD,
  useSettingsFunctionRolesVM
} from '../lib/vm/useSettingsFunctionRolesVM';
import { formatRelativeTime } from '../lib/relativeTime';
import './SettingsFunctionRoles.css';

const SOFT_CAP_NOTE = `You've defined ${SOFT_CAP_THRESHOLD} roles — consider consolidating.`;
const LAYERING_CAPTION =
  'The agent receives these three sections concatenated, in this order. More specific layers refine earlier ones.';
const ORG_STANDARDS_NOTE =
  'Org-wide standards (Layer 1) will render here once settings-org-standards ships. They apply to every role.';
const PER_STATUS_NOTE =
  'At runtime this layer is filled in for the specific (document type, document status, optional role override). Authored in settings-documents.';
const MEMBER_READ_ONLY_NOTE =
  'You can read the rules so you understand how your agent will behave. Owners and Admins author them.';
const SYSTEM_DELETE_NOTE =
  'System roles cannot be deleted. To use a different slug, create a custom role.';
const RULE_ARCHIVED_NOTE =
  'This rule is archived. Members in this role fall back to the platform default. Restore to re-enable editing.';

function memberCountLabel(n: number): string {
  return `${n} member${n === 1 ? '' : 's'}`;
}

function membershipCountLabel(n: number): string {
  return `${n} membership${n === 1 ? '' : 's'}`;
}

/**
 * `/settings/function-roles` (merged page, 2026-05-17). One screen that
 * defines what a function role is AND the agent rule that loads for it via
 * MCP. Two-column layout: roles on the left, single inline editor panel on
 * the right with Name, Slug (read-only), Description, Agent rule, Preview
 * disclosure, History disclosure, Members + MCP stats, and editor actions.
 * System roles are fully editable for label/description/agent rule (slug
 * stays immutable); only custom roles can be deleted.
 */
export function SettingsFunctionRoles() {
  const vm = useSettingsFunctionRolesVM();

  return (
    <section
      className="set-fr-page"
      data-testid="settings-function-roles"
      data-status={vm.status}
      data-viewer-role={vm.viewerRole ?? ''}
    >
      <SettingsBackLink />
      {vm.status === 'loading' ? (
        <div className="set-fr-loading" data-testid="settings-fr-loading">
          <Skeleton height={48} />
          <Skeleton height={360} />
        </div>
      ) : null}

      {vm.status === 'error' ? (
        <Banner kind="error" data-testid="settings-fr-error">
          {vm.error ?? "Couldn't load function roles. Try again."}
        </Banner>
      ) : null}

      {vm.status === 'unauth' ? (
        <Banner kind="default" data-testid="settings-fr-unauth">
          You need to sign in to view function roles.
        </Banner>
      ) : null}

      {vm.status === 'empty' ? (
        <Banner kind="default" data-testid="settings-fr-empty">
          You don't belong to any org yet.
        </Banner>
      ) : null}

      {vm.status === 'ready' ? <ReadyView vm={vm} /> : null}
    </section>
  );
}

function ReadyView({
  vm
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
}) {
  const showSoftCap =
    vm.customRoleCount >= SOFT_CAP_THRESHOLD && !vm.softCapDismissed;

  return (
    <>
      {showSoftCap ? (
        <Banner kind="default" data-testid="settings-fr-soft-cap">
          <span className="set-fr-soft-cap-text">{SOFT_CAP_NOTE}</span>
          <Button
            variant="ghost"
            onClick={vm.actions.dismissSoftCap}
            data-testid="settings-fr-soft-cap-dismiss"
          >
            Dismiss
          </Button>
        </Banner>
      ) : null}

      <header className="set-fr-head">
        <h1 className="set-fr-title">Function roles</h1>
        <p className="set-fr-lede">
          Define what people in your org do and the agent rule the user's
          agent loads for that role via MCP. One screen, two halves.
        </p>
      </header>

      <div className="set-fr-grid">
        <aside className="set-fr-list" data-testid="settings-fr-list">
          <RoleSection
            title="System"
            roles={vm.systemRoles}
            selectedId={vm.selectedId}
            onSelect={vm.actions.select}
            testid="settings-fr-system"
          />
          <RoleSection
            title="Custom"
            roles={vm.customRoles}
            selectedId={vm.selectedId}
            onSelect={vm.actions.select}
            emptyNote="No custom roles yet."
            testid="settings-fr-custom"
          />
          {vm.includeArchived && vm.archivedRoles.length > 0 ? (
            <RoleSection
              title="Archived"
              roles={vm.archivedRoles}
              selectedId={vm.selectedId}
              onSelect={vm.actions.select}
              testid="settings-fr-archived"
            />
          ) : null}
          {vm.canManage ? (
            <div className="set-fr-create">
              <Button
                variant="ghost"
                onClick={vm.actions.openCreate}
                data-testid="settings-fr-create-trigger"
              >
                + Create role
              </Button>
            </div>
          ) : null}
          <div className="set-fr-show-archived">
            <Checkbox
              label="Include archived"
              checked={vm.includeArchived}
              onChange={vm.actions.toggleIncludeArchived}
              data-testid="settings-fr-include-archived"
            />
          </div>
        </aside>

        <div className="set-fr-editor" data-testid="settings-fr-editor">
          {vm.selectedRole ? (
            <RoleEditor vm={vm} />
          ) : (
            <p className="set-fr-empty-note">Select a role to view it.</p>
          )}
        </div>
      </div>

      <CreateRoleModal vm={vm} />
      <RenameConfirmModal vm={vm} />
      <ArchiveConfirmModal vm={vm} />

      {vm.saveToast ? (
        <Toast onDismiss={vm.actions.clearSaveToast}>{vm.saveToast}</Toast>
      ) : null}
      {vm.deleteToast ? (
        <Toast
          title="Role deleted"
          durationMs={6000}
          onDismiss={vm.actions.clearDeleteToast}
        >
          {`Deleted “${vm.deleteToast}”.`}
        </Toast>
      ) : null}
      {vm.archiveToast ? (
        <Toast onDismiss={vm.actions.clearArchiveToast}>
          {vm.archiveToast}
        </Toast>
      ) : null}
      {vm.restoreToast ? (
        <Toast onDismiss={vm.actions.clearRestoreToast}>
          {vm.restoreToast}
        </Toast>
      ) : null}
    </>
  );
}

function RoleSection({
  title,
  roles,
  selectedId,
  onSelect,
  emptyNote,
  testid
}: {
  title: string;
  roles: FunctionRole[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  emptyNote?: string;
  testid: string;
}) {
  return (
    <div className="set-fr-section" data-testid={testid}>
      <MonoLabel
        as="div"
        size="md"
        tracking="wide"
        className="set-fr-section-head"
      >
        {title}
      </MonoLabel>
      {roles.length === 0 && emptyNote ? (
        <p className="set-fr-section-empty">{emptyNote}</p>
      ) : (
        roles.map((r) => {
          const status = resolveRuleStatus(r.agentRule);
          return (
            <NavListItem
              key={r.id}
              label={r.label}
              meta={`${memberCountLabel(r.memberCount)} · ${ruleStatusLabel(status)}`}
              selected={r.id === selectedId}
              onClick={() => onSelect(r.id)}
              data-testid={`settings-fr-role-${r.key}`}
            />
          );
        })
      )}
    </div>
  );
}

function RoleEditor({
  vm
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
}) {
  const role = vm.selectedRole as FunctionRole;
  const isSystem = role.kind === 'system';
  const canManage = vm.canManage;
  const status = resolveRuleStatus(role.agentRule);
  const archived = status === 'archived';

  const draftLabel = vm.draft?.label ?? role.label;
  const draftDescription = vm.draft?.description ?? role.description ?? '';
  const ruleValue = vm.editorRule;
  const charCount = ruleValue.length;
  const overWarn = charCount > RULE_SOFT_WARN;
  const showStartFromDefault = canManage && !archived && ruleValue.trim() === '';

  return (
    <div className="set-fr-editor-body" data-testid="settings-fr-editor-body">
      <div className="set-fr-editor-head">
        <Tag>{isSystem ? 'System' : 'Custom'}</Tag>
        <h2 className="set-fr-editor-title">{role.label}</h2>
        <Tag data-testid="settings-fr-status-chip">
          {ruleStatusLabel(status)}
        </Tag>
        <MonoLabel
          as="span"
          size="sm"
          tone="muted"
          className="set-fr-last-prop"
          data-testid="settings-fr-last-propagated"
        >
          {`Last propagated: ${
            role.agentRule.lastPropagatedAt
              ? formatRelativeTime(role.agentRule.lastPropagatedAt)
              : '—'
          }`}
        </MonoLabel>
      </div>

      <TextField
        id="settings-fr-label"
        label="Name"
        value={canManage && !isSystem ? draftLabel : role.label}
        readOnly={!canManage || isSystem}
        onChange={
          canManage && !isSystem
            ? (e) => vm.actions.editDraft({ label: e.target.value })
            : undefined
        }
        maxLength={canManage && !isSystem ? 40 : undefined}
        helpText={isSystem ? 'System role names are fixed.' : undefined}
        data-testid="settings-fr-label"
      />
      <TextField
        id="settings-fr-key"
        label="Slug"
        value={role.key}
        readOnly
        helpText="Stable identifier — immutable after creation."
        data-testid="settings-fr-key"
      />
      {canManage ? (
        <TextAreaField
          id="settings-fr-description-input"
          label="Description (optional, ≤ 500 chars)"
          value={draftDescription}
          onChange={(e) =>
            vm.actions.editDraft({ description: e.target.value })
          }
          maxLength={500}
          rows={3}
          data-testid="settings-fr-description-input"
        />
      ) : (
        <div className="set-fr-desc" data-testid="settings-fr-description">
          <span className="set-fr-desc-label">Description</span>
          <p className="set-fr-desc-body">
            {role.description ?? 'No description set.'}
          </p>
        </div>
      )}

      <RuleField
        vm={vm}
        ruleValue={ruleValue}
        canManage={canManage}
        archived={archived}
        charCount={charCount}
        overWarn={overWarn}
        showStartFromDefault={showStartFromDefault}
      />

      <PreviewSection role={role} ruleValue={ruleValue} archived={archived} />
      <HistorySection vm={vm} role={role} />

      <div className="set-fr-stats" data-testid="settings-fr-stats">
        <span data-testid="settings-fr-stats-members">
          {memberCountLabel(role.memberCount)}
        </span>
        <span aria-hidden="true">·</span>
        <span data-testid="settings-fr-stats-mcp">
          {`MCP: ${ruleStatusLabel(status)}`}
        </span>
      </div>

      {canManage ? (
        <EditorActions vm={vm} role={role} archived={archived} />
      ) : (
        <Banner kind="default" data-testid="settings-fr-readonly-note">
          {MEMBER_READ_ONLY_NOTE}
        </Banner>
      )}
    </div>
  );
}

function RuleField({
  vm,
  ruleValue,
  canManage,
  archived,
  charCount,
  overWarn,
  showStartFromDefault
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
  ruleValue: string;
  canManage: boolean;
  archived: boolean;
  charCount: number;
  overWarn: boolean;
  showStartFromDefault: boolean;
}) {
  if (!canManage) {
    const renderContent =
      ruleValue.trim() === '' || archived ? HARDCODED_DEFAULT_RULE : ruleValue;
    return (
      <div className="set-fr-rule-readonly">
        <span className="set-fr-desc-label">Agent rule</span>
        <Markdown
          className="set-fr-readonly-render"
          dataTestId="settings-fr-rule-readonly"
        >
          {renderContent}
        </Markdown>
      </div>
    );
  }
  return (
    <div className="set-fr-rule">
      <TextAreaField
        id="settings-fr-rule"
        label="Agent rule (markdown)"
        className="set-fr-rule-field"
        value={ruleValue}
        onChange={(e) => vm.actions.editDraft({ agentRule: e.target.value })}
        rows={24}
        disabled={archived}
        placeholder="Write the agent rule for this role…"
        data-testid="settings-fr-rule-textarea"
      />
      <div className="set-fr-rule-meta">
        {showStartFromDefault ? (
          <Button
            variant="ghost"
            onClick={vm.actions.startFromDefault}
            data-testid="settings-fr-start-default"
          >
            Start from default
          </Button>
        ) : (
          <span />
        )}
        <MonoLabel
          as="div"
          size="sm"
          tone={overWarn ? 'fg' : 'muted'}
          data-testid="settings-fr-char-counter"
          data-warn={overWarn ? 'true' : 'false'}
        >
          {`${charCount} chars${overWarn ? ' · Long rules eat agent context window.' : ''}`}
        </MonoLabel>
      </div>
      {archived ? (
        <Banner kind="default" data-testid="settings-fr-rule-archived-note">
          {RULE_ARCHIVED_NOTE}
        </Banner>
      ) : null}
    </div>
  );
}

function PreviewSection({
  role,
  ruleValue,
  archived
}: {
  role: FunctionRole;
  ruleValue: string;
  archived: boolean;
}) {
  const usingWorking = ruleValue.trim() !== '' && !archived;
  const roleContent = usingWorking ? ruleValue : HARDCODED_DEFAULT_RULE;
  const roleLabel = usingWorking ? role.label : 'platform default';
  const merged = [
    '## Org standards',
    ORG_STANDARDS_NOTE,
    '',
    `## Role rule (${roleLabel})`,
    roleContent,
    '',
    '## Per-status (placeholder)',
    PER_STATUS_NOTE
  ].join('\n');

  return (
    <div className="set-fr-preview">
      <Disclosure
        key={role.id}
        summary="Preview merged context"
        dataTestId="settings-fr-preview"
      >
        <p className="set-fr-preview-caption">{LAYERING_CAPTION}</p>
        <Markdown
          className="set-fr-merged"
          dataTestId="settings-fr-merged"
        >
          {merged}
        </Markdown>
      </Disclosure>
    </div>
  );
}

type HistoryModal =
  | { kind: 'view' | 'diff' | 'restore'; version: RoleRuleVersion }
  | null;

function HistorySection({
  vm,
  role
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
  role: FunctionRole;
}) {
  const versions = vm.selectedRuleVersions;
  const [modal, setModal] = useState<HistoryModal>(null);

  // Reset modal when switching roles.
  useEffect(() => {
    setModal(null);
  }, [role.id]);

  const authorHandle = (userId: string | null): string =>
    (userId && vm.handles[userId]) || 'someone';

  const restoredFromLabel = (v: RoleRuleVersion): string | null => {
    if (!v.restoredFromVersionId) return null;
    const src = versions.find((x) => x.id === v.restoredFromVersionId);
    if (!src) return null;
    return `(restored from ${formatRelativeTime(src.createdAt)})`;
  };

  return (
    <div className="set-fr-versions">
      <Disclosure
        key={role.id}
        summary={`Agent-rule history (${versions.length})`}
        dataTestId="settings-fr-versions"
      >
        {vm.restoreError ? (
          <Banner kind="error" data-testid="settings-fr-restore-error">
            {vm.restoreError}
          </Banner>
        ) : null}
        {versions.length === 0 ? (
          <p
            className="set-fr-versions-empty"
            data-testid="settings-fr-versions-empty"
          >
            No prior versions yet. Save the rule to create the first snapshot.
          </p>
        ) : (
          <table className="set-fr-versions-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">By</th>
                <th scope="col">Length</th>
                <th scope="col">
                  <span className="set-fr-sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => {
                const restored = restoredFromLabel(v);
                return (
                  <tr
                    key={v.id}
                    data-testid={`settings-fr-version-${v.versionNumber}`}
                  >
                    <td className="set-fr-versions-when">
                      <span>{formatRelativeTime(v.createdAt)}</span>
                      {restored ? (
                        <span className="set-fr-versions-restored-from">
                          {restored}
                        </span>
                      ) : null}
                    </td>
                    <td className="set-fr-versions-by">
                      {authorHandle(v.createdBy)}
                    </td>
                    <td className="set-fr-versions-len">
                      {`${v.content.length} chars`}
                    </td>
                    <td className="set-fr-versions-actions">
                      <Button
                        variant="ghost"
                        onClick={() => setModal({ kind: 'view', version: v })}
                        data-testid={`settings-fr-version-view-${v.versionNumber}`}
                      >
                        View
                      </Button>
                      {vm.canManage ? (
                        <>
                          <Button
                            variant="ghost"
                            onClick={() =>
                              setModal({ kind: 'diff', version: v })
                            }
                            data-testid={`settings-fr-version-diff-${v.versionNumber}`}
                          >
                            Diff vs current
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() =>
                              setModal({ kind: 'restore', version: v })
                            }
                            disabled={vm.restoringVersionId !== null}
                            data-testid={`settings-fr-version-restore-${v.versionNumber}`}
                          >
                            Restore
                          </Button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Disclosure>

      <Modal
        open={modal?.kind === 'view'}
        onClose={() => setModal(null)}
        title={
          modal
            ? `Version from ${formatRelativeTime(
                modal.version.createdAt
              )} · by ${authorHandle(modal.version.createdBy)}`
            : 'Version'
        }
        dataTestId="settings-fr-view-modal"
        footer={
          <Button
            variant="ghost"
            onClick={() => setModal(null)}
            data-testid="settings-fr-view-close"
          >
            Close
          </Button>
        }
      >
        <pre
          className="set-fr-view-version"
          data-testid="settings-fr-view-content"
        >
          {modal?.version.content ?? ''}
        </pre>
      </Modal>

      <Modal
        open={modal?.kind === 'diff'}
        onClose={() => setModal(null)}
        title={
          modal
            ? `Version ${formatRelativeTime(modal.version.createdAt)} vs current`
            : 'Version diff'
        }
        dataTestId="settings-fr-diff-modal"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModal(null)}
              data-testid="settings-fr-diff-close"
            >
              Close
            </Button>
            {vm.canManage && modal ? (
              <Button
                variant="primary"
                onClick={() =>
                  setModal({ kind: 'restore', version: modal.version })
                }
                data-testid="settings-fr-diff-restore"
              >
                Restore this version
              </Button>
            ) : null}
          </>
        }
      >
        {modal ? (
          <DiffView
            before={modal.version.content}
            after={role.agentRule.content}
            beforeLabel="Prior version"
            afterLabel="Current"
            dataTestId="settings-fr-diff-view"
          />
        ) : null}
      </Modal>

      <Modal
        open={modal?.kind === 'restore'}
        onClose={() => setModal(null)}
        title="Restore this version?"
        dataTestId="settings-fr-restore-modal"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModal(null)}
              data-testid="settings-fr-restore-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (modal) void vm.actions.restoreRuleVersion(modal.version.id);
                setModal(null);
              }}
              data-testid="settings-fr-restore-confirm"
            >
              Restore
            </Button>
          </>
        }
      >
        The current content will be replaced with this version. A new snapshot
        of the current state is appended to history first, so the restore is
        reversible.
      </Modal>
    </div>
  );
}

function EditorActions({
  vm,
  role,
  archived
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
  role: FunctionRole;
  archived: boolean;
}) {
  const canDelete = role.kind === 'custom';

  return (
    <div className="set-fr-editor-actions" data-testid="settings-fr-editor-actions">
      {vm.saveError ? (
        <Banner kind="error" data-testid="settings-fr-save-error">
          {vm.saveError}
        </Banner>
      ) : null}
      {vm.archiveError ? (
        <Banner kind="error" data-testid="settings-fr-archive-error">
          {vm.archiveError}
        </Banner>
      ) : null}
      <div className="set-fr-editor-buttons">
        {role.agentRule.ruleId !== null ? (
          archived ? (
            <Button
              variant="ghost"
              onClick={() => void vm.actions.restoreRule()}
              disabled={vm.archiveStatus === 'saving'}
              className="set-fr-archive-btn"
              data-testid="settings-fr-restore-rule"
            >
              Restore rule
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={vm.actions.openArchiveConfirm}
              disabled={vm.archiveStatus === 'saving'}
              className="set-fr-archive-btn"
              data-testid="settings-fr-archive-rule"
            >
              Archive rule
            </Button>
          )
        ) : null}
        <Button
          variant="ghost"
          onClick={vm.actions.cancelEdit}
          disabled={!vm.isDirty}
          data-testid="settings-fr-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void vm.actions.save()}
          disabled={!vm.isDirty || vm.isSaving}
          data-testid="settings-fr-save"
        >
          {vm.isSaving && vm.saveStatus !== 'confirm' ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {canDelete ? (
        <DeletePanel vm={vm} role={role} />
      ) : (
        <Banner kind="default" data-testid="settings-fr-system-note">
          {SYSTEM_DELETE_NOTE}
        </Banner>
      )}
    </div>
  );
}

function DeletePanel({
  vm,
  role
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
  role: FunctionRole;
}) {
  const { deleteStatus } = vm;
  const [confirmLabel, setConfirmLabel] = useState('');

  useEffect(() => {
    setConfirmLabel('');
  }, [deleteStatus, role.id]);

  if (deleteStatus === 'idle') {
    return (
      <div className="set-fr-delete">
        <Button
          variant="ghost"
          onClick={vm.actions.openDelete}
          data-testid="settings-fr-delete-trigger"
        >
          Delete role
        </Button>
      </div>
    );
  }

  if (deleteStatus === 'blocked') {
    return (
      <div className="set-fr-delete" data-testid="settings-fr-delete-blocked">
        <Banner kind="error">
          {`${memberCountLabel(role.memberCount)} assigned to ${role.label}. Reassign them via Members first.`}
        </Banner>
        <div className="set-fr-delete-actions">
          <Button
            variant="ghost"
            onClick={vm.actions.closeDelete}
            data-testid="settings-fr-delete-cancel"
          >
            Cancel
          </Button>
          <ButtonRouterLink
            to="/members"
            variant="primary"
            data-testid="settings-fr-delete-open-members"
          >
            Open Members →
          </ButtonRouterLink>
        </div>
      </div>
    );
  }

  const deleting = deleteStatus === 'deleting';
  const matches = confirmLabel === role.label;

  return (
    <div className="set-fr-delete" data-testid="settings-fr-delete-confirm">
      <p className="set-fr-delete-prompt">
        Type <strong>{role.label}</strong> to confirm deletion. This can't be
        undone.
      </p>
      {vm.deleteError ? (
        <Banner kind="error" data-testid="settings-fr-delete-error">
          {vm.deleteError}
        </Banner>
      ) : null}
      <TextField
        id="settings-fr-delete-input"
        label="Role label"
        value={confirmLabel}
        onChange={(e) => setConfirmLabel(e.target.value)}
        autoComplete="off"
        data-testid="settings-fr-delete-input"
      />
      <div className="set-fr-delete-actions">
        <Button
          variant="ghost"
          onClick={vm.actions.closeDelete}
          disabled={deleting}
          data-testid="settings-fr-delete-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void vm.actions.deleteRole(confirmLabel)}
          disabled={!matches || deleting}
          data-testid="settings-fr-delete-submit"
        >
          {deleting ? 'Deleting…' : 'Delete role'}
        </Button>
      </div>
    </div>
  );
}

function RenameConfirmModal({
  vm
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
}) {
  const open = vm.saveStatus === 'confirm';
  const saving = vm.isSaving;
  const count = vm.selectedRole?.memberCount ?? 0;

  return (
    <Modal
      open={open}
      onClose={vm.actions.closeRenameConfirm}
      title="Confirm rename"
      dataTestId="settings-fr-rename-modal"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={vm.actions.closeRenameConfirm}
            disabled={saving}
            data-testid="settings-fr-rename-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void vm.actions.confirmSave()}
            disabled={saving}
            data-testid="settings-fr-rename-confirm"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      {vm.saveError ? (
        <Banner kind="error" data-testid="settings-fr-rename-error">
          {vm.saveError}
        </Banner>
      ) : null}
      <p className="set-fr-rename-copy" data-testid="settings-fr-rename-copy">
        {`Renaming will update ${membershipCountLabel(count)}.`}
      </p>
    </Modal>
  );
}

function ArchiveConfirmModal({
  vm
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
}) {
  const open = vm.archiveStatus === 'confirm';
  const role = vm.selectedRole;
  return (
    <Modal
      open={open}
      onClose={vm.actions.closeArchiveConfirm}
      title="Archive this rule?"
      dataTestId="settings-fr-archive-modal"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={vm.actions.closeArchiveConfirm}
            data-testid="settings-fr-archive-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void vm.actions.archiveRule();
            }}
            data-testid="settings-fr-archive-confirm"
          >
            Archive
          </Button>
        </>
      }
    >
      {`Members in role "${role?.label ?? ''}" will fall back to the platform default until the rule is restored.`}
    </Modal>
  );
}

function CreateRoleModal({
  vm
}: {
  vm: ReturnType<typeof useSettingsFunctionRolesVM>;
}) {
  const open = vm.createStatus !== 'closed';
  const saving = vm.createStatus === 'saving';
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [agentRule, setAgentRule] = useState('');

  useEffect(() => {
    if (open) {
      setKey('');
      setLabel('');
      setDescription('');
      setAgentRule('');
    }
  }, [open]);

  const err = vm.createError;
  const keyError = err?.field === 'key' ? err.message : undefined;
  const labelError = err?.field === 'label' ? err.message : undefined;
  const formError = err?.field === 'form' ? err.message : undefined;

  const submit = () => {
    void vm.actions.createRole({ key, label, description, agentRule });
  };

  return (
    <Modal
      open={open}
      onClose={vm.actions.closeCreate}
      title="Create a custom function role"
      dataTestId="settings-fr-create-modal"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={vm.actions.closeCreate}
            data-testid="settings-fr-create-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={saving}
            data-testid="settings-fr-create-submit"
          >
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <form
        className="set-fr-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {formError ? (
          <Banner kind="error" data-testid="settings-fr-create-form-error">
            {formError}
          </Banner>
        ) : null}
        <TextField
          id="settings-fr-create-key"
          label="Slug (immutable)"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          maxLength={32}
          placeholder="e.g. platform-engineer"
          autoComplete="off"
          helpText="2–32 lowercase chars: a–z, 0–9, dash. Cannot be changed later."
          errorText={keyError}
          data-testid="settings-fr-create-key"
        />
        <TextField
          id="settings-fr-create-label"
          label="Display label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={40}
          placeholder="e.g. Platform Engineer"
          autoComplete="off"
          errorText={labelError}
          data-testid="settings-fr-create-label"
        />
        <TextAreaField
          id="settings-fr-create-description"
          label="Description (optional, ≤ 500 chars)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          rows={3}
          data-testid="settings-fr-create-description"
        />
        <TextAreaField
          id="settings-fr-create-rule"
          label="Agent rule (optional, markdown)"
          value={agentRule}
          onChange={(e) => setAgentRule(e.target.value)}
          rows={6}
          placeholder="Leave empty to start with the platform default."
          data-testid="settings-fr-create-rule"
        />
      </form>
    </Modal>
  );
}
